/**
 * QAILoginPage (F3) - v2.3 Simplified
 * Clean, professional login page for Q AI users
 *
 * Route: /q-ai/login (public)
 * Target: Existing Q AI users (email/password or Google OAuth)
 *
 * v2.3 Changes:
 * - Simplified layout: email/password form as primary
 * - Google sign-in as secondary option (JWT credential flow)
 * - Clean, professional styling
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { GoogleLogin } from '@react-oauth/google';
import QAILogo from '../../Components/QAI/QAILogo';
import { googleLogin, emailLogin } from '../../api/qaiApi';

// ═══════════════════════════════════════════════════════════════════════════════
// BRAND CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const BRAND = {
  primary: '#27B56D',
  primaryLight: '#22C55E',
  primaryDark: '#16A34A',
  background: '#0A0A0F',
  surface: '#14141A',
  text: '#FFFFFF',
  textSecondary: '#E2E8F0',
  textMuted: '#94A3B8',
  textDim: '#64748B',
  border: 'rgba(255, 255, 255, 0.12)',
  borderLight: 'rgba(255, 255, 255, 0.18)',
  glow: 'rgba(39, 181, 109, 0.4)',
  error: '#EF4444'
};

/**
 * QAILoginPage Component
 */
const QAILoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Handle Google OAuth success - receives JWT credential (ID token)
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setIsGoogleLoading(true);
      setError(null);

      // Send JWT credential to backend for authentication
      const { token, user } = await googleLogin(credentialResponse.credential, 'qai');

      // Store Q AI specific tokens
      localStorage.setItem('qai_token', token);
      localStorage.setItem('qai_user', JSON.stringify(user));

      // Navigate to coach page
      navigate('/q-ai/coach');
    } catch (err) {
      console.error('Google login error:', err);
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  // Handle Google OAuth error
  const handleGoogleError = () => {
    console.error('Google OAuth error');
    setError('Google sign-in failed. Please try again.');
    setIsGoogleLoading(false);
  };

  // Handle email/password login
  const handleEmailLogin = async (event) => {
    event.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setIsLoading(true);
    try {
      const result = await emailLogin(email.trim(), password);
      console.log('Login result:', result);

      const { token, user } = result;
      if (!token) {
        throw new Error('No token received from server');
      }

      console.log('Storing token:', token.substring(0, 20) + '...');
      localStorage.setItem('qai_token', token);
      localStorage.setItem('qai_user', JSON.stringify(user));
      navigate('/q-ai/coach');
    } catch (err) {
      console.error('Email login error:', err);
      setError(err.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: BRAND.background,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Back Navigation */}
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/q-ai')}
          sx={{
            color: BRAND.textMuted,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              color: BRAND.text,
              backgroundColor: 'transparent'
            }
          }}
        >
          Back to Q AI
        </Button>
      </Box>

      {/* Main Content */}
      <Container
        maxWidth="xs"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 2, sm: 4 },
          px: { xs: 2, sm: 3 }
        }}
      >
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: 380 } }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <QAILogo size="large" animated />
          </Box>

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              color: BRAND.text,
              fontWeight: 700,
              textAlign: 'center',
              mb: 1
            }}
          >
            Sign In
          </Typography>

          <Typography
            sx={{
              color: BRAND.textMuted,
              fontSize: '0.9375rem',
              textAlign: 'center',
              mb: 4
            }}
          >
            Welcome back to Q AI
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: BRAND.error,
                borderRadius: '8px',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                '& .MuiAlert-icon': { color: BRAND.error }
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Email/Password Form */}
          <Box component="form" onSubmit={handleEmailLogin}>
            {/* Email Field */}
            <TextField
              fullWidth
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading || isGoogleLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: BRAND.textDim, fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                mb: 1.5,  // Standardized: consistent input spacing (6px)
                '& .MuiOutlinedInput-root': {
                  backgroundColor: BRAND.surface,
                  borderRadius: '8px',
                  color: BRAND.text,
                  '& fieldset': { borderColor: BRAND.border },
                  '&:hover fieldset': { borderColor: BRAND.borderLight },
                  '&.Mui-focused fieldset': { borderColor: BRAND.primary, borderWidth: '1px' }
                },
                '& input::placeholder': { color: BRAND.textDim, opacity: 1 },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: `0 0 0 100px ${BRAND.surface} inset`,
                  WebkitTextFillColor: BRAND.text
                }
              }}
            />

            {/* Password Field */}
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading || isGoogleLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: BRAND.textDim, fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: BRAND.textDim, '&:hover': { color: BRAND.textMuted } }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                mb: 2,  // Standardized: spacing before button (8px)
                '& .MuiOutlinedInput-root': {
                  backgroundColor: BRAND.surface,
                  borderRadius: '8px',
                  color: BRAND.text,
                  '& fieldset': { borderColor: BRAND.border },
                  '&:hover fieldset': { borderColor: BRAND.borderLight },
                  '&.Mui-focused fieldset': { borderColor: BRAND.primary, borderWidth: '1px' }
                },
                '& input::placeholder': { color: BRAND.textDim, opacity: 1 },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: `0 0 0 100px ${BRAND.surface} inset`,
                  WebkitTextFillColor: BRAND.text
                }
              }}
            />

            {/* Sign In Button */}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isLoading || isGoogleLoading || !email || !password}
              sx={{
                py: 1.5,
                backgroundColor: BRAND.primary,
                color: '#fff',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: BRAND.primaryLight
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(39, 181, 109, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </Box>

          {/* Divider */}
          <Divider
            sx={{
              my: { xs: 2, sm: 2.5 },  // Tighter on mobile (8px), balanced on desktop (10px)
              '&::before, &::after': { borderColor: BRAND.border }
            }}
          >
            <Typography sx={{ color: BRAND.textDim, fontSize: '0.8125rem', px: 2 }}>
              or
            </Typography>
          </Divider>

          {/* Google Sign In - JWT Credential Flow */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            mb: { xs: 1.5, sm: 2 },  // Tighter spacing below Google button
            '& > div': {
              width: '100%',
              maxWidth: { xs: '100%', sm: 380 },
              '& > div': { width: '100% !important' }  // Override Google's inline width
            }
          }}>
            {isGoogleLoading ? (
              <Button
                fullWidth
                variant="outlined"
                disabled
                sx={{
                  py: { xs: 1.25, sm: 1.5 },
                  borderColor: BRAND.border,
                  color: BRAND.textDim,
                  textTransform: 'none',
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  fontWeight: 500,
                  borderRadius: '8px'
                }}
              >
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Signing in...
              </Button>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="filled_blue"
                size="large"
                text="continue_with"
                shape="rectangular"
              />
            )}
          </Box>

          {/* Platform Login Link */}
          <Box sx={{ textAlign: 'center' }}>  {/* No extra margin - Google button has mb */}
            <Typography
              component="span"
              sx={{ color: BRAND.textDim, fontSize: '0.875rem' }}
            >
              QTradeView platform user?{' '}
            </Typography>
            <Typography
              component="span"
              onClick={() => navigate('/login')}
              sx={{
                color: BRAND.primary,
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Platform Login
            </Typography>
          </Box>

          {/* Signup Link */}
          <Box sx={{ textAlign: 'center', mt: 1 }}>  {/* Tight: 4px spacing */}
            <Typography
              component="span"
              sx={{ color: BRAND.textDim, fontSize: '0.875rem' }}
            >
              Don't have an account?{' '}
            </Typography>
            <Typography
              component="span"
              onClick={() => navigate('/q-ai/signup')}
              sx={{
                color: BRAND.primary,
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Sign up free
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Footer Trust Indicators */}
      <Box
        sx={{
          py: 3,
          borderTop: `1px solid ${BRAND.border}`
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: { xs: 3, md: 5 },
              flexWrap: 'wrap'
            }}
          >
            {[
              { icon: <SecurityIcon sx={{ fontSize: 16 }} />, text: 'Secure Login' },
              { icon: <SpeedIcon sx={{ fontSize: 16 }} />, text: 'Instant Access' },
              { icon: <AutoAwesomeIcon sx={{ fontSize: 16 }} />, text: 'AI-Powered' }
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  color: BRAND.textDim
                }}
              >
                <Box sx={{ color: BRAND.primary, display: 'flex' }}>
                  {item.icon}
                </Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default QAILoginPage;
